import { ApiError, call, fetchAllPages, fetchPage, type PageReq } from '../../shared/api.ts'
export { ApiError }
import type { apiDto } from '@rs/contracts'
/**
 * ══ CỬA GỌI REST CỦA DANH MỤC ĐỐI TÁC (T5) ═══════════════════════════════════════════════════
 * Bản cũ đọc/ghi thẳng vào `useEntityCollection` (kho trong trình duyệt). rs-erp gọi máy chủ, nên
 * mọi luật đã nằm ở đó — màn này KHÔNG kiểm quyền, KHÔNG kiểm MST: nó chỉ hiển thị lỗi máy chủ trả
 * về. Kiểm lại ở đây là dựng bản sao thứ hai của luật rồi hai bản lệch nhau.
 */

/**
 * ⚠ KIỂU LẤY TỪ HỢP ĐỒNG, KHÔNG CHÉP TAY (KIEN-TRUC §12.3). Trước đợt N1, mỗi hình dạng dưới đây
 *   là một `interface` viết tay ở đây, song song với một `interface` khác bên `packages/modules`.
 *   Không có gì buộc hai bản khớp: đổi tên một trường ở máy chủ thì `tsc` vẫn xanh và màn hình
 *   lặng lẽ hiện `undefined`. Nay chỉ còn MỘT bản, ở `@rs/contracts`.
 */
export type CustomerRow = apiDto.CustomerDto



const BASE = '/api/v1/catalog/customers'

/**
 * Danh sách GỌN — đổ dropdown cho hàng chục màn khác. Đọc HẾT mọi trang, và đó là đúng: một
 * dropdown phải có đủ lựa chọn.
 *
 * ⚠ BẢNG Danh mục KHÔNG dùng hàm này nữa (xem `fetchCustomerBoard`). Cửa `…/bang` mới là cửa có
 *   lọc · sắp xếp · cắt trang ở máy chủ, và nó kèm sẵn tình trạng hợp đồng + dư nợ của từng dòng.
 */
export const fetchCustomers = (): Promise<CustomerRow[]> => fetchAllPages<CustomerRow>(BASE)

/* ══ BẢNG DANH MỤC — lọc và cắt trang Ở MÁY CHỦ (§8.3) ═══════════════════════════════════════ */

export type CatalogCustomerRow = apiDto.CatalogCustomerDto
export type CatalogVendorRow = apiDto.CatalogVendorDto
export type BoardSummary = apiDto.PartnerBoardSummaryDto
export type PartnerKpiFilter = 'noContract' | 'pending' | 'expiring' | 'overdue'

/** Bộ lọc người dùng đang bật — CÙNG một bộ cho cả ba cửa (danh sách · bảng · tóm tắt). */
export interface BoardFilters {
  q: string
  kpi: PartnerKpiFilter | null
}

/**
 * ⚠ Ô tìm kiếm RỖNG thì KHÔNG gửi `q` lên. Lược đồ khai `z.string().trim().min(1)`, nên gửi chuỗi
 *   rỗng là 400 — màn đỏ ngay lần mở đầu tiên, lúc chưa ai gõ gì.
 */
const boardQuery = (f: BoardFilters): string => {
  const p = new URLSearchParams()
  if (f.q.trim() !== '') p.set('q', f.q.trim())
  if (f.kpi !== null) p.set('kpi', f.kpi)
  return p.toString()
}

const withQuery = (base: string, f: BoardFilters): string => {
  const q = boardQuery(f)
  return q === '' ? base : `${base}?${q}`
}

export const fetchCustomerBoard = (
  f: BoardFilters, req: PageReq,
): Promise<apiDto.PageOf<CatalogCustomerRow>> =>
  fetchPage<CatalogCustomerRow>(withQuery(`${BASE}/bang`, f), req)

export const fetchCustomerBoardSummary = (f: BoardFilters): Promise<BoardSummary> =>
  call<BoardSummary>(withQuery(`${BASE}/tom-tat`, f))

/**
 * ⬇ XUẤT EXCEL — đọc HẾT mọi trang của ĐÚNG bộ lọc đang bật.
 *
 * ⚠ CỐ Ý không xuất "trang đang xem": bộ lọc là câu hỏi người dùng vừa đặt, và tệp phải trả lời
 *   đúng câu đó (bản cũ chốt 23/08). Trần 100 trang của `fetchAllPages` vẫn nguyên — chạm trần thì
 *   báo lỗi có câu chữ, không cắt câm.
 */
export const fetchAllCustomerBoard = (f: BoardFilters): Promise<CatalogCustomerRow[]> =>
  fetchAllPages<CatalogCustomerRow>(withQuery(`${BASE}/bang`, f))

export const fetchVendorBoard = (
  f: BoardFilters, req: PageReq,
): Promise<apiDto.PageOf<CatalogVendorRow>> =>
  fetchPage<CatalogVendorRow>(withQuery(`${VENDORS}/bang`, f), req)

export const fetchVendorBoardSummary = (f: BoardFilters): Promise<BoardSummary> =>
  call<BoardSummary>(withQuery(`${VENDORS}/tom-tat`, f))

export const fetchAllVendorBoard = (f: BoardFilters): Promise<CatalogVendorRow[]> =>
  fetchAllPages<CatalogVendorRow>(withQuery(`${VENDORS}/bang`, f))

export const createCustomerApi = (body: Record<string, unknown>): Promise<CustomerRow> =>
  call<CustomerRow>(BASE, { method: 'POST', body: JSON.stringify(body) })

export const updateCustomerApi = (
  id: string, version: number, patch: Record<string, unknown>,
): Promise<CustomerRow> =>
  call<CustomerRow>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify({ ...patch, version }) })


/**
 * Tra MST → tên công ty & địa chỉ. Máy chủ LUÔN trả 200; `found: false` nghĩa là không tra được
 * và người dùng gõ tay — không phải lỗi. Xem `packages/modules/partner/src/infra/tax-lookup.ts`.
 */
export const lookupTaxApi = (tax: string): Promise<apiDto.TaxLookupDto> =>
  call<apiDto.TaxLookupDto>(`/api/v1/catalog/tra-mst?tax=${encodeURIComponent(tax)}`)

export type UserRow = apiDto.UserRefDto
export const fetchUsers = (): Promise<UserRow[]> => fetchAllPages<UserRow>('/api/v1/users')
/** CẢ người đã ngừng — hộp Sale & chia sẻ của bản cũ không lọc `active` (D02113 · D02140 · D02143). */
export const fetchUsersAll = (): Promise<UserRow[]> => fetchAllPages<UserRow>('/api/v1/users?includeInactive=1')

/** Trình tỷ lệ mới. Máy chủ TỰ QUYẾT có cần duyệt hay không — màn không đoán. */
export const submitSplitApi = (id: string, version: number, body: Record<string, unknown>): Promise<void> =>
  call<void>(`${BASE}/${id}/sales-split`, { method: 'POST', body: JSON.stringify({ ...body, version }) })

export const decideSplitApi = (id: string, version: number, approve: boolean): Promise<void> =>
  call<void>(`${BASE}/${id}/sales-split/decide`, { method: 'POST', body: JSON.stringify({ version, approve }) })

export type VendorRow = apiDto.VendorDto

const VENDORS = '/api/v1/catalog/vendors'

export const fetchVendors = (): Promise<VendorRow[]> => fetchAllPages<VendorRow>(VENDORS)

export const createVendorApi = (body: Record<string, unknown>): Promise<VendorRow> =>
  call<VendorRow>(VENDORS, { method: 'POST', body: JSON.stringify(body) })

/** ⇤ Gộp NCC trùng — `dryRun` chỉ đếm số dòng sẽ trỏ lại ở từng bảng (`bảng.cột`). */
export const mergeVendorsApi = (
  keepId: string, dropIds: readonly string[], dryRun: boolean,
): Promise<{ counts: Record<string, number>; dropped: string[] }> =>
  call(`${VENDORS}/gop`, { method: 'POST', body: JSON.stringify({ keepId, dropIds, dryRun }) })

export const deleteVendorApi = (id: string, version: number): Promise<void> =>
  call<void>(`${VENDORS}/${id}/delete`, { method: 'POST', body: JSON.stringify({ version }) })

export const updateVendorApi = (
  id: string, version: number, patch: Record<string, unknown>,
): Promise<VendorRow> =>
  call<VendorRow>(`${VENDORS}/${id}`, { method: 'PATCH', body: JSON.stringify({ ...patch, version }) })

export type ContractRow = apiDto.ContractDto

const CONTRACTS = '/api/v1/catalog/contracts'

/** Phía của một hợp đồng — BÁN (khách) hay MUA (thầu phụ). Xem migration `0061`. */
export type ContractSide = 'customer' | 'vendor'

/**
 * Hợp đồng của MỘT đối tác, hoặc CẢ danh sách.
 *
 * BỎ TRỐNG cả hai ⇒ lấy hết trong một lời gọi — cửa máy chủ khai tham số đó là tuỳ chọn
 * (`ListContractQuery`). Bốn ô KPI của danh mục cần biết trạng thái hợp đồng của MỌI đối tác; hỏi
 * từng người là hàng trăm lời gọi để vẽ bốn con số.
 *
 * ⚠ `side` LỌC Ở MÁY CHỦ. Hai màn đọc cùng một bảng, và lọc ở màn là chỗ để quên — quên thì hợp
 *   đồng MUA hiện lẫn trong danh sách BÁN.
 */
export const fetchContracts = (
  partyId = '', side: ContractSide = 'customer',
): Promise<ContractRow[]> => {
  const p = new URLSearchParams()
  if (partyId !== '') p.set(side === 'vendor' ? 'vendorId' : 'customerId', partyId)
  p.set('side', side)
  return fetchAllPages<ContractRow>(`${CONTRACTS}?${p.toString()}`)
}

export const createContractApi = (body: Record<string, unknown>): Promise<ContractRow> =>
  call<ContractRow>(CONTRACTS, { method: 'POST', body: JSON.stringify(body) })

/**
 * Đính thẳng một hợp đồng ĐÃ KÝ — cửa riêng, KHÔNG phải `createContractApi` kèm cờ. Xem chú thích
 * `attachSignedContract` ở `@rs/contract` về lý do hai cửa.
 */
export const attachSignedContractApi = (body: Record<string, unknown>): Promise<ContractRow> =>
  call<ContractRow>(`${CONTRACTS}/dinh-ban-goc`, { method: 'POST', body: JSON.stringify(body) })

/**
 * Mỗi BƯỚC quy trình một cửa — KHÔNG một hàm chung nhận `status`. Máy chủ cũng dựng y vậy
 * (`apps/api/src/routes/contracts.ts`): cho lời gọi tự đặt `status` là bỏ qua toàn bộ luật chuyển
 * trạng thái VÀ phép kiểm "ai được duyệt".
 */
const contractStep = (
  id: string, name: string, version: number, body: Record<string, unknown> = {},
): Promise<void> =>
  call<void>(`${CONTRACTS}/${id}${name === '' ? '' : `/${name}`}`,
    { method: 'POST', body: JSON.stringify({ ...body, version }) })

export const updateContractApi = (id: string, version: number, patch: Record<string, unknown>): Promise<void> =>
  contractStep(id, '', version, patch)
export const proposeContractApi = (
  id: string, version: number, proposeNote: string, assignedApproverId: string | null,
): Promise<void> => contractStep(id, 'propose', version, { proposeNote, assignedApproverId })
export const decideContractApi = (
  id: string, version: number, approve: boolean, reason?: string,
): Promise<void> =>
  contractStep(id, 'decide', version, { approve, ...(reason === undefined ? {} : { reason }) })
/** "Sửa lại & trình lại" — hợp đồng bị trả về quay lại bản soạn (bản cũ `AzContract.tsx:1247-1249`). */
export const reopenContractApi = (id: string, version: number): Promise<void> =>
  contractStep(id, 'reopen', version)
/** Số HĐ gợi ý theo phía — bản cũ `suggestContractNo` (hop-dong-kh-8). */
export const fetchContractNoSuggest = (side: ContractSide): Promise<{ contractNo: string }> =>
  call<{ contractNo: string }>(`/api/v1/catalog/contracts/so-goi-y?side=${side}`)
export const fillContractDetailsApi = (
  id: string, version: number, body: Record<string, unknown>,
): Promise<void> => contractStep(id, 'details', version, body)
/** Hộp đính HĐ gốc nhanh — ghi MỘT ô (hop-dong-kh-9). Trả `version` mới để ô kế tiếp ghi tiếp được. */
export const patchContractQuickApi = (
  id: string, version: number, patch: { contractNo?: string; validFrom?: string; validTo?: string },
): Promise<{ version: number }> =>
  call<{ version: number }>(`${CONTRACTS}/${id}/o-nhanh`, { method: 'POST', body: JSON.stringify({ ...patch, version }) })
export const markContractSignedApi = (id: string, version: number, signDate: string): Promise<void> =>
  contractStep(id, 'signed', version, { signDate })
export const handoverContractApi = (id: string, version: number): Promise<void> =>
  contractStep(id, 'handover', version)
export const receiveContractApi = (
  id: string, version: number, receivedDate: string, receivedBy?: string,
  /** `true` = nút ở dòng bảng Gia hạn — ghi kỳ gia hạn mới nhất; vắng = khối bàn giao — ghi hợp đồng GỐC (hop-dong-kh-6). */
  renewal?: true,
): Promise<void> =>
  contractStep(id, 'receive', version, {
    receivedDate, ...(receivedBy === undefined ? {} : { receivedBy }), ...(renewal === undefined ? {} : { renewal }),
  })

/**
 * CHỜ BỔ SUNG — kế toán trả về một bản gốc đã cầm nhưng chưa dùng được (scan mờ, thiếu phụ lục).
 * HAI cửa chứ không một cửa nhận cờ bật/tắt: đặt cờ ĐÒI lý do, gỡ cờ thì không.
 */
export const markSupplementApi = (id: string, version: number, reason: string): Promise<void> =>
  contractStep(id, 'cho-bo-sung', version, { reason })
export const clearSupplementApi = (id: string, version: number): Promise<void> =>
  contractStep(id, 'da-bo-sung', version)

/* ══ GIA HẠN · PHỤ LỤC · TRAO ĐỔI (`0124`) ═══════════════════════════════════════════════════ */

/**
 * Báo giá ĐÃ CHỐT, rút gọn cho ô "tạo phụ lục từ báo giá".
 * ⚠ `Pick` TỪ `apiDto.QuoteDto` chứ không gõ lại ba ô: đổi hình dạng báo giá ở hợp đồng thì chỗ này
 *   phải đỏ ngay, không im lặng lệch đi (cổng `web-dto`).
 */
export type QuoteRowLite = Pick<apiDto.QuoteDto, 'id' | 'ref' | 'revenues' | 'sellPrice'>

export type ContractRenewal = apiDto.ContractRenewalDto
export type ContractAppendix = apiDto.ContractAppendixDto
export type ContractComment = apiDto.ContractCommentDto

/**
 * MỌI báo giá của một khách — nguồn của "＋ Tạo phụ lục từ báo giá". Bản cũ `myQuotes =
 * quotes.items.filter(q => q.customerId === customer.id)` (`AzContract.tsx:1050`) KHÔNG lọc trạng
 * thái: báo giá nháp/đã gửi cũng tạo được phụ lục (D01970 · D02079).
 */
export const fetchQuotesOf = async (customerId: string): Promise<QuoteRowLite[]> => {
  const rows = await fetchAllPages<QuoteRowLite>(`/api/v1/az/quotes?customerId=${customerId}`)
  return rows.map((q) => ({ id: q.id, ref: q.ref, revenues: q.revenues, sellPrice: q.sellPrice }))
}

export const renewContractApi = (
  id: string, version: number, body: { validFrom: string; validTo: string; note?: string },
): Promise<void> => contractStep(id, 'gia-han', version, body)

/** ⚠ KHÔNG gửi số phụ lục — máy chủ đánh số (xem `AppendixBody`). */
export const addAppendixApi = (
  id: string, version: number,
  body: { body: string; templateId?: string; sourceQuoteRef?: string; syncPricesFromQuoteId?: string },
): Promise<ContractAppendix> =>
  call<ContractAppendix>(`${CONTRACTS}/${id}/phu-luc`,
    { method: 'POST', body: JSON.stringify({ ...body, version }) })

/** Sửa NGÀY NHẬN bản gốc sau khi đã nhận (L34, bản cũ "Ngày nhận (sửa được)"). */
export const setReceivedDateApi = (id: string, version: number, receivedDate: string): Promise<void> =>
  contractStep(id, 'ngay-nhan', version, { receivedDate })

export const updateAppendixApi = (
  id: string, appendixId: string, version: number, body: string,
): Promise<void> =>
  call<void>(`${CONTRACTS}/${id}/phu-luc/${appendixId}`,
    { method: 'POST', body: JSON.stringify({ body, version }) })

export const commentContractApi = (id: string, version: number, text: string): Promise<ContractComment> =>
  call<ContractComment>(`${CONTRACTS}/${id}/trao-doi`,
    { method: 'POST', body: JSON.stringify({ text, version }) })

/** ✉ Ghi nhận đã gửi email tệp của một ngăn — nhật ký `sendLog` + lịch sử (bản cũ `onSend`, `AzContract.tsx:1334`). */
export const logContractSendApi = (
  id: string, body: { field: string; to: string; note?: string },
): Promise<void> =>
  call<void>(`${CONTRACTS}/${id}/gui-email`, { method: 'POST', body: JSON.stringify(body) })

/** 🔗 Chuỗi rỗng = gỡ link. */
export const setContractShareUrlApi = (id: string, version: number, shareUrl: string): Promise<void> =>
  contractStep(id, 'link-chia-se', version, { shareUrl })

export type ContractPriceRow = apiDto.ContractPriceDto

/** 🔗 Link Drive bảng giá của một đối tác — bản cũ `az-price-links` / `az-vendor-price-links` (`AzContract.tsx:1729`). */
export const fetchPriceLink = async (partyId: string, side: ContractSide): Promise<string> =>
  (await call<{ url: string }>(`/api/v1/catalog/contract-prices/link-drive?${side === 'vendor' ? 'vendorId' : 'customerId'}=${partyId}`)).url
export const savePriceLinkApi = (partyId: string, side: ContractSide, url: string): Promise<void> =>
  call<void>('/api/v1/catalog/contract-prices/link-drive', {
    method: 'POST', body: JSON.stringify({ [side === 'vendor' ? 'vendorId' : 'customerId']: partyId, url }),
  })

const PRICES = '/api/v1/catalog/contract-prices'

/** ⚠ Cửa máy chủ nhận `customerId` HOẶC `vendorId` — một bảng, hai phía (`0021`). */
export const fetchContractPrices = (
  partyId: string, side: ContractSide = 'customer',
): Promise<ContractPriceRow[]> =>
  fetchAllPages<ContractPriceRow>(
    `${PRICES}?${side === 'vendor' ? 'vendorId' : 'customerId'}=${encodeURIComponent(partyId)}`)

export const createContractPriceApi = (body: Record<string, unknown>): Promise<ContractPriceRow> =>
  call<ContractPriceRow>(PRICES, { method: 'POST', body: JSON.stringify(body) })

export const updateContractPriceApi = (
  id: string, version: number, patch: Record<string, unknown>,
): Promise<void> =>
  call<void>(`${PRICES}/${id}`, { method: 'POST', body: JSON.stringify({ ...patch, version }) })

/** Xoá HẲN — bản cũ có nút 🗑️. `version` bắt buộc: xoá nhầm bản người khác vừa sửa cũng là mất việc. */
export const deleteContractPriceApi = (id: string, version: number): Promise<void> =>
  call<void>(`${PRICES}/${id}/delete`, { method: 'POST', body: JSON.stringify({ version }) })

/* ══ ĐỊNH KHOẢN ĐÃ NHỚ THEO NCC (`0068`) ═════════════════════════════════════════════════════ */

export type VendorPostingRow = apiDto.VendorPostingDto

const VP = '/api/v1/catalog/dinh-khoan-ncc'

export const fetchVendorPostings = (vendorId = ''): Promise<VendorPostingRow[]> =>
  fetchAllPages<VendorPostingRow>(`${VP}${vendorId === '' ? '' : `?vendorId=${vendorId}`}`)

export const saveVendorPostingApi = (body: Record<string, unknown>): Promise<VendorPostingRow> =>
  call<VendorPostingRow>(VP, { method: 'POST', body: JSON.stringify(body) })

export const deleteVendorPostingApi = (id: string, version: number): Promise<void> =>
  call<void>(`${VP}/${id}/delete`, { method: 'POST', body: JSON.stringify({ version }) })
